import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  Clock,
  MapPin,
  Shirt,
  BookOpen,
  ArrowRight,
  Box,
  Tag,
  Layers,
  ScrollText,
  Scale,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à L'Étang-la-Ville ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de fournitures directement au sec chez vous
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que les routes vallonnées et arborées de
              L&apos;Étang-la-Ville ne facilitent pas le transport
              de paquets encombrants dans une voiture citadine, notre
              service logistique s&apos;occupe de tout.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Un transport de proximité",
                body: "Nous acheminons l'intégralité de vos commandes à plat sur le pas de votre porte, aussi bien à L'Étang-la-Ville que dans les communes voisines comme Marly-le-Roi, Mareil-Marly, Saint-Nom-la-Bretèche et Noisy-le-Roi.",
                Icon: MapPin,
              },
              {
                title: "Une flexibilité totale",
                body: "Vous choisissez un créneau horaire précis selon vos disponibilités. Vos cartons professionnels vous sont remis dans un état de propreté irréprochable, totalement secs et prêts à être montés pour attaquer vos pièces immédiatement.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Formats de cartons spécifiques ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Formats ciblés
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des formats de cartons spécifiques pour chaque objet de la
            maison
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            On ne remplit pas un carton de livres de la même manière
            qu&apos;un service de table en porcelaine. Notre boutique
            propose des formats ciblés pour sécuriser chaque catégorie
            de biens.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Standard */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Box className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                Le carton standard
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Idéal pour le linge de maison, les jouets ou le petit
              électroménager, il offre un volume polyvalent et des
              poignées de transport renforcées.
            </p>
            <Link
              to="/cartons-demenagement"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[13px] transition-colors"
              data-testid="city-letang-la-ville-cta-standard"
            >
              Voir le carton standard
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          {/* Livres */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <BookOpen className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                Le carton livres &amp; charges lourdes
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Plus petit et doté d&apos;une double cannelure
              ultra-rigide, il empêche le fond de céder sous le poids
              de vos archives ou de vos ouvrages de bibliothèque.
            </p>
            <Link
              to="/cartons-demenagement"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[13px] transition-colors"
              data-testid="city-letang-la-ville-cta-livres"
            >
              Voir le carton livres
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          {/* Penderie */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Shirt className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                Le carton penderie
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Un modèle vertical équipé d&apos;une tringle métallique
              pour suspendre vos costumes et robes directement sur
              cintres sans les froisser.
            </p>
            <Link
              to="/cartons-demenagement"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[13px] transition-colors"
              data-testid="city-letang-la-ville-cta-penderie"
            >
              Voir le carton penderie
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Guide préparation (4 conseils) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Le guide pour une préparation de cartons sans aucune fausse
            note
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour optimiser l&apos;espace dans le camion de déménagement
            et accélérer votre installation, voici les bons réflexes à
            adopter lors de la mise en boîte.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            {
              num: "01",
              title: "Le calage des vides",
              body: "Comblez systématiquement les espaces restants avec notre papier bulle pour empêcher les objets de bouger et de s'entrechoquer sur la route.",
              Icon: Layers,
              accent: "#237cb5",
            },
            {
              num: "02",
              title: "La répartition du poids",
              body: "Placez toujours les éléments les plus lourds au fond et les plus légers au-dessus pour équilibrer la boîte et éviter l'écrasement.",
              Icon: Scale,
              accent: "#237cb5",
            },
            {
              num: "03",
              title: "Le marquage visible",
              body: "Écrivez la pièce cible et le contenu au marqueur sur les côtés du carton pour que les déménageurs sachent où les déposer.",
              Icon: ScrollText,
              accent: "#FF5722",
            },
            {
              num: "04",
              title: "Le scellage en croix",
              body: "Appliquez votre ruban adhésif professionnel sur le fond en croisant les bandes afin de renforcer la structure et éliminer le risque d'ouverture au levage.",
              Icon: Tag,
              accent: "#FF5722",
            },
          ].map(({ num, title, body, Icon, accent }) => (
            <div
              key={num}
              className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 overflow-hidden"
            >
              <span
                className="absolute top-0 left-0 h-1.5 w-full"
                style={{ backgroundColor: accent }}
              />
              <div className="flex items-baseline gap-3">
                <span
                  className="font-display font-extrabold text-[34px] leading-none"
                  style={{ color: accent }}
                >
                  {num}
                </span>
                <span
                  className="flex items-center justify-center h-9 w-9 rounded-lg text-white"
                  style={{ backgroundColor: accent }}
                >
                  <Icon className="h-4.5 w-4.5" strokeWidth={2.4} />
                </span>
              </div>
              <h3 className="mt-4 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-letang-la-ville-cta-bulles"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir le papier bulle
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-letang-la-ville-cta-adhesif"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; marqueurs
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-letang-la-ville-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
